// Global search modal with CMD+K shortcut
// Notion-style command palette for searching journal entries

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, X, Clock, Lightbulb } from 'lucide-react';
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut';
import { useSearch } from '../../hooks/useSearch';
import { trackSearchExecuted } from '../../utils/analytics/analytics';
import SearchResults from './SearchResults';
import SearchFilters from './SearchFilters';

/**
 * SearchModal - Global search interface
 *
 * Features:
 * - CMD/Ctrl+K to open
 * - Full-text search
 * - Recent searches
 * - Advanced filters
 * - Keyboard navigation
 * - Mobile responsive
 */
export default function SearchModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);

  const {
    query,
    results,
    suggestions,
    recentSearches,
    isSearching,
    hasResults,
    hasQuery,
    filters,
    updateQuery,
    updateFilters,
    clearFilter,
    clearSearch,
    executeSearch,
    clearRecentSearches,
    removeRecentSearch,
    addRecentSearch
  } = useSearch();

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Keyboard shortcuts
  useKeyboardShortcut('escape', () => {
    if (showFilters) {
      setShowFilters(false);
    } else {
      onClose();
    }
  }, {
    enabled: isOpen,
    allowInInput: true
  });

  // Navigate to entry
  const handleSelectEntry = (entry) => {
    addRecentSearch(query);
    onClose();
    navigate(`/journal/${entry.id}`);
  };

  // Handle search from recent/suggestion
  const handleQuickSearch = (searchQuery) => {
    executeSearch(searchQuery);
    // Track search after results are populated
    setTimeout(() => {
      trackSearchExecuted(results.length, Object.keys(filters).length > 0);
    }, 500);
    inputRef.current?.focus();
  };

  // Clear all
  const handleClear = () => {
    clearSearch();
    setShowFilters(false);
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  const activeFilterCount = Object.keys(filters).length;
  const showRecentSearches = !hasQuery && recentSearches.length > 0;
  const showSuggestions = query.length > 0 && query.length < 2 && suggestions.length > 0;
  const showEmpty = hasQuery && !isSearching && !hasResults;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal - Bottom sheet on mobile, centered on desktop */}
      <div className="fixed inset-0 z-50 flex items-end sm:items-start sm:justify-center sm:pt-[8dvh] pointer-events-none">
        <div
          className="bg-[#1a1410] border border-white/10 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[85dvh] overflow-hidden pointer-events-auto animate-slideDown"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="search-title"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Header */}
          <div className="border-b border-white/5">
            {/* Search Input */}
            <div className="flex items-center gap-3 p-3">
              <Search size={18} className="text-white/25 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => updateQuery(e.target.value)}
                placeholder="Search entries..."
                className="flex-1 bg-transparent text-base text-white placeholder:text-white/25 outline-none font-light"
                aria-label="Search journal entries"
                autoComplete="off"
                spellCheck="false"
              />

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`
                  relative p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg transition-all
                  ${showFilters || activeFilterCount > 0
                    ? 'bg-white/10 text-white'
                    : 'text-white/25 hover:text-white/70 hover:bg-white/5'
                  }
                `}
                aria-label="Toggle filters"
                title="Filters"
              >
                <SlidersHorizontal size={16} />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#ffb85e] rounded-full" />
                )}
              </button>

              {/* Clear/Close Button */}
              <button
                onClick={hasQuery || activeFilterCount > 0 ? handleClear : onClose}
                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-white/25 hover:text-white/70 hover:bg-white/5 transition-all"
                aria-label={hasQuery || activeFilterCount > 0 ? "Clear search" : "Close"}
              >
                <X size={16} />
              </button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <SearchFilters
                filters={filters}
                onUpdateFilters={updateFilters}
                onClearFilter={clearFilter}
              />
            )}

            {/* Stats Bar - Minimal */}
            {hasQuery && (
              <div className="px-5 py-1.5 text-xs text-white/25 font-light">
                {isSearching ? (
                  'Searching...'
                ) : hasResults ? (
                  `${results.length} ${results.length === 1 ? 'result' : 'results'}`
                ) : (
                  'No results'
                )}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(85dvh - 100px)', WebkitOverflowScrolling: 'touch' }}>
            {/* Recent Searches */}
            {showRecentSearches && (
              <div className="px-5 py-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs text-white/25 uppercase tracking-wide">
                    Recent
                  </h3>
                  <button
                    onClick={clearRecentSearches}
                    className="text-xs text-white/20 hover:text-white/60 transition-colors"
                  >
                    Clear
                  </button>
                </div>
                <div className="space-y-0.5">
                  {recentSearches.map((search) => (
                    <div
                      key={search}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-white/5 transition-colors group"
                    >
                      <button
                        onClick={() => handleQuickSearch(search)}
                        className="flex items-center gap-2 flex-1 text-left text-sm text-white/70 font-light"
                      >
                        <Clock size={12} className="text-white/20" />
                        {search}
                      </button>
                      <button
                        onClick={() => removeRecentSearch(search)}
                        className="opacity-0 group-hover:opacity-100 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-white/20 hover:text-white/60 transition-all"
                        aria-label="Remove"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search Suggestions */}
            {showSuggestions && (
              <div className="px-5 py-3">
                <h3 className="text-xs text-white/25 uppercase tracking-wide mb-2">
                  Suggestions
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => handleQuickSearch(suggestion)}
                      className="px-2.5 py-1 min-h-[36px] bg-white/5 hover:bg-white/10 text-xs text-white/70 rounded-lg transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Loading State */}
            {isSearching && (
              <div className="flex items-center justify-center py-10">
                <div className="flex items-center gap-2 text-white/25">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white/60" />
                  <span className="text-xs">Searching...</span>
                </div>
              </div>
            )}

            {/* Search Results */}
            {hasResults && !isSearching && (
              <SearchResults
                results={results}
                query={query}
                onSelectEntry={handleSelectEntry}
              />
            )}

            {/* Empty State */}
            {showEmpty && (
              <div className="flex flex-col items-center justify-center py-10 px-5 text-center">
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-3">
                  <Search size={20} className="text-white/20" />
                </div>
                <h3 className="text-sm font-light text-white/70 mb-1">
                  No results found
                </h3>
                <p className="text-xs text-white/25 mb-3">
                  Try different keywords or adjust filters
                </p>
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => {
                      updateFilters({});
                      setShowFilters(false);
                    }}
                    className="text-xs text-[#ffb85e]/70 hover:text-[#ffb85e] transition-colors"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {/* Help/Tutorial State */}
            {!hasQuery && recentSearches.length === 0 && (
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mx-auto mb-3">
                  <Lightbulb size={20} className="text-white/20" />
                </div>
                <h3 className="text-sm font-light text-white/70 mb-2">
                  Search your journal
                </h3>
                <p className="text-xs text-white/25 mb-4">
                  Find entries by content, mood, or date
                </p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  <button
                    onClick={() => handleQuickSearch('happy')}
                    className="px-2.5 py-1 min-h-[36px] bg-white/5 hover:bg-white/10 text-xs text-white/30 rounded-lg transition-colors"
                  >
                    happy
                  </button>
                  <button
                    onClick={() => handleQuickSearch('grateful')}
                    className="px-2.5 py-1 min-h-[36px] bg-white/5 hover:bg-white/10 text-xs text-white/30 rounded-lg transition-colors"
                  >
                    grateful
                  </button>
                  <button
                    onClick={() => handleQuickSearch('work')}
                    className="px-2.5 py-1 min-h-[36px] bg-white/5 hover:bg-white/10 text-xs text-white/30 rounded-lg transition-colors"
                  >
                    work
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
